<style lang="scss" scoped>
.fake-input {
  pointer-events: none;
  border: solid 1px lightgrey;
  border-right: 0;
  text-indent: 5px;
  max-width: 185px;
}
</style>
<template>
  <!-- eslint-disable max-len -->
  <div class="container">
    <div v-if="!loading" class="theme-color-wrapper p-2 booking-slots-page">
      <div class="calls row">
        <section class="col-12">
          <h2 cy="page-title" class="font-weight-bolderer mb-2">Update In-Room Booking Slot</h2>
          <router-link :to="{ name: 'in-cell-visit-booking-slots'}" class="body-text">
            &laquo; Back to in-room booking slots
          </router-link>
        </section>
      </div>

      <div class="row my-2">
        <div class="slot-stage-wrapper">
          <div class="col-12">
            <section class="slot-stage">
              <h4 class="font-weight-bolder">Slot Name</h4>

              <div class="form-group full-width d-flex pb-2">
                <input v-model="bookingSlotName"
                       cy="slot-name"
                       class="form-control"
                       type="text"
                       placeholder="Slot Name"
                >
              </div>
            </section>

            <section class="slot-stage">
              <h4 class="pb-1">When should the slot start?</h4>
              <p>Slots are up to {{ max_slot_length }} minutes long. Calls are terminated when the slot ends</p>

              <div class="clock-custom d-flex" style="font-size: 18pt" cy="start-time">
                <div>
                  <i class="fas fa-chevron-up d-block"
                     @click="increaseTimeSlot('slotBeginHour', hours, updateEndSlot)"
                  />
                  <span class="d-block">{{ slotBeginHour }} :</span>
                  <i class="fas fa-chevron-down d-block"
                     @click="decreaseTimeSlot('slotBeginHour', hours, updateEndSlot)"
                  />
                </div>

                <div class="ml-1">
                  <i class="fas fa-chevron-up d-block"
                     @click="increaseTimeSlot('slotBeginMin', mins, updateEndSlot)"
                  />
                  <span class="d-block">{{ slotBeginMin }}</span>
                  <i class="fas fa-chevron-down d-block"
                     @click="decreaseTimeSlot('slotBeginMin', mins, updateEndSlot)"
                  />
                </div>
              </div>

              <p class="pb-2 mt-2 font-weight-bolder">
                This slot will start at: <b>{{ slotBeginHour }}:{{ slotBeginMin }}</b>
                <span v-if="limitedSlot"
                      class="px-1 d-inline-block"
                >and end at: <b>{{ slotEndHour }}:{{ slotEndMin }}</b></span>
              </p>
            </section>

            <section v-if="!limitedSlot" class="slot-stage">
              <h4 class="pb-2">When should the slot end?</h4>

              <b-alert v-if="!endDateValid" show variant="danger" class="font-weight-bolder p-2">
                <i class="fas fa-exclamation-triangle mr-2"></i>Please ensure the end time is after the start time and within the {{ max_slot_length }} minute duration.
              </b-alert>

              <div class="clock-custom d-flex" style="font-size: 18pt" cy="end-time">
                <div>
                  <i class="fas fa-chevron-up d-block"
                     @click="increaseTimeSlot('slotEndHour', hours, checkEndSlotValid)"
                  />
                  <span class="d-block">{{ slotEndHour }} :</span>
                  <i class="fas fa-chevron-down d-block"
                     @click="decreaseTimeSlot('slotEndHour', hours, checkEndSlotValid)"
                  />
                </div>

                <div class="ml-1">
                  <i class="fas fa-chevron-up d-block"
                     @click="increaseTimeSlot('slotEndMin', mins, checkEndSlotValid)"
                  />
                  <span class="d-block">{{ slotEndMin }}</span>
                  <i class="fas fa-chevron-down d-block"
                     @click="decreaseTimeSlot('slotEndMin', mins, checkEndSlotValid)"
                  />
                </div>
              </div>

              <p class="pb-2 mt-2 font-weight-bolder" cy="slot-text">
                This slot will start at: <b>{{ slotBeginHour }}:{{ slotBeginMin }}</b>
                <span class="px-1 d-inline-block">and end at: <b>{{ slotEndHour }}:{{ slotEndMin }}</b></span>
              </p>
            </section>

            <section class="slot-stage">
              <h4 class="pb-2 pt-2">Do you want to apply a date range?</h4>

              <b-form-checkbox v-model="applyDateRange" name="check-button" switch @change="resetStartEndDate()">
                Apply date range
              </b-form-checkbox>

              <section v-if="applyDateRange">
                <p class="pt-3">Select a start and end date</p>
                <div>
                  <label>Start Date</label>
                  <b-form-datepicker
                    v-model="start_date"
                    :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
                    @input="end_date = ''"
                  />
                  <label>End Date</label>
                  <b-form-datepicker
                    v-model="end_date"
                    :min="start_date"
                    :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
                  />
                </div>
              </section>

            </section>

            <section class="slot-stage">
              <button :class="{'disabled': applyDateRange && !endDateValid}" class="btn btn-block btn-success mt-2"
                      @click="displayBookingSlotInfoModal()"
              >
                Change booking slot (This will make the slot available for bookings)
              </button>
            </section>
          </div>
        </div>
      </div>
    </div>
    <div v-else>
      <p>Loading...</p>
    </div>
  </div>
  <!-- eslint-enable -->
</template>

<script>
import moment from 'moment';
import { mapState } from 'vuex';
import { ERROR_CODES } from '@/util/errorCodes';
import InCellBookingSlotsService from '../../services/InCellBookingSlotsService';

/* eslint-disable prefer-destructuring */

export default {
  name: 'UpdateInCellBookingSlot',
  data() {
    return {
      bookingSlotId: '',
      loading: true,
      // bookingSlotDescription: '',
      bookingSlot: null,
      bookingSlotActive: null,
      bookingSlotName: '',
      slotBeginHour: '',
      slotBeginMin: '',
      slotEndHour: '',
      slotEndMin: '',
      start_date: '',
      end_date: '',
      weekDays: InCellBookingSlotsService.getWeekdays(),
      hours: InCellBookingSlotsService.getHours(),
      mins: InCellBookingSlotsService.getMinutes(),
      endDateValid: true,
      applyDateRange: false,
    };
  },
  computed: mapState({
    prison: state => state.prison.prison,
    limitedSlot: state => (state.prison.prison ? state.prison.prison.limitedSlot : ''),
    max_slot_length: state => (state.prison.prison ? state.prison.prison.max_slot_length : ''),
    allBookingSlots: state => state.inCellBookingSlots.bookingSlots,
  }),
  watch: {
    limitedSlot() {
      this.updateEndSlot();
    },
  },
  async mounted() {
    await this.$store.dispatch('prison/getPrison');
    this.bookingSlotId = this.$route.params.bookingSlotId;
    this.getBookingSlot();
    await this.$store.dispatch('inCellBookingSlots/getBookingSlots');
  },
  methods: {
    getBookingSlot() {
      InCellBookingSlotsService.getBookingSlotSingle(this.bookingSlotId).then(res => {
        this.bookingSlot = res.data;

        this.bookingSlotName = this.bookingSlot.name;
        this.bookingSlotActive = this.bookingSlot.active ? 'active' : 'inActive';
        this.slotBeginHour = this.bookingSlot.slot_begin.split(':')[0];
        // eslint-disable-next-line prefer-destructuring
        this.slotBeginMin = this.bookingSlot.slot_begin.split(':')[1];
        this.slotEndHour = this.bookingSlot.slot_end.split(':')[0];
        this.slotEndMin = this.bookingSlot.slot_end.split(':')[1];
        this.start_date = this.bookingSlot.start_date ? moment(this.bookingSlot.start_date).format('YYYY-MM-DD') : '';
        this.end_date = this.bookingSlot.end_date ? moment(this.bookingSlot.end_date).format('YYYY-MM-DD') : '';

        if (this.start_date) {
          this.applyDateRange = true;
        }

        this.loading = false;
      }).catch(err => {
        console.log('Error getting booking slot ', err);
        this.$toast.error('Error getting the booking slot', {
          toastClassName: ['toast-std', 'warning-toast'],
        });
        this.$router.go(-1);
      });
    },
    validUpdate() {
      return this.bookingSlotName && this.bookingSlotName.length > 0;
    },
    displayBookingSlotInfoModal() {
      this.$swal({
        title: 'Existing bookings will not be changed',
        text: 'This change is only valid for new bookings. Existing bookings must be honoured in their original time.',
        icon: 'info',
        buttons: true,
      }).then(value => {
        if (value) {
          this.checkDuplicateBookingSlots();
        }
      });
    },
    checkDuplicateBookingSlots() {
      let duplicateSlots = [];

      const slotBegin = `${this.slotBeginHour}:${this.slotBeginMin}`;
      const slotEnd = `${this.slotEndHour}:${this.slotEndMin}`;

      duplicateSlots = InCellBookingSlotsService.getDuplicatedDates(this.allBookingSlots, this.bookingSlot.day_of_week, slotBegin, slotEnd, this.bookingSlot._id);
      duplicateSlots = InCellBookingSlotsService.filterDuplicatesByDateRange(duplicateSlots, {
        start_date: this.start_date,
        end_date: this.end_date,
      });
      if (duplicateSlots[0]) { // warn if duplicates, otherwise create
        this.displayBookingSlotOverlapModal(duplicateSlots[0], duplicateSlots);
      } else {
        this.updateBookingSlot();
      }
    },
    displayBookingSlotOverlapModal(slot, slots) {
      const name = InCellBookingSlotsService.getOverlapName(slot);

      this.$swal({
        title: `This overlaps with an existing slot ${name}`,
        text: 'Do you want to overwrite the existing slot?',
        icon: 'warning',
        buttons: true,
      }).then(value => {
        if (value) {
          // delete overlap
          const slotClone = { ...slot };
          slotClone.active = false;
          InCellBookingSlotsService.updateBookingSlot(slotClone._id, slotClone).then(() => { // Changed to InCellBookingSlotsService
            this.manageUpdatingSlots(slot, slots);
          }).catch(err => {
            console.log('Error deactivating overlapping booking slot ', err.response);
            let errorMessage = 'Error deactivating the overlapping booking slot';
            if (err && err.response && err.response.data) {
              if (err.response.data.error === ERROR_CODES.BOOKING_SLOTS.OVERLAP) {
                errorMessage = 'Error deactivating the overlapping booking slot: Booking slot overlaps with another active slot.';
              } else if (err.response.data.message) {
                errorMessage = err.response.data.message;
              } else if (err.response.data.error) {
                errorMessage = err.response.data.error;
              }
            }
            this.$toast.error(errorMessage, {
              toastClassName: ['toast-std', 'warning-toast'],
            });
            // Stop the process here if deactivation of overlapping slot fails
            this.$router.push('/in-cell-booking-slots');
          });
        } else {
          this.$toast.info('Booking slot not updated');
          this.$router.push({ name: 'in-cell-visit-booking-slots' });
        }
      });
    },
    manageUpdatingSlots(slot, slots) {
      const newSlots = slots.splice(1);

      if (newSlots[0]) {
        this.displayBookingSlotOverlapModal(newSlots[0], newSlots);
      } else {
        this.updateBookingSlot();
      }
    },
    updateBookingSlot() {
      if (this.validUpdate()) {
        this.$toast.info('Updating booking slot...');

        const updateDto = { ...this.bookingSlot };

        updateDto.name = this.bookingSlotName;
        updateDto.active = this.bookingSlotActive === 'active';
        updateDto.slot_begin = `${this.slotBeginHour}:${this.slotBeginMin}`;
        updateDto.slot_end = `${this.slotEndHour}:${this.slotEndMin}`;
        updateDto.start_date = this.applyDateRange && this.start_date ? this.start_date : null;
        updateDto.end_date = this.applyDateRange && this.end_date ? this.end_date : null;

        console.log('Attempting to update booking slot with ID:', this.bookingSlotId);
        console.log('Update DTO:', updateDto);

        InCellBookingSlotsService.updateBookingSlot(this.bookingSlotId, updateDto).then(response => {
          console.log('Booking slot update successful:', response);
          this.$toast.success('Booking slot successfully updated', {
            toastClassName: ['toast-std', 'success-toast'],
          });
          this.$router.push({ name: 'in-cell-visit-booking-slots' });
        }).catch(err => {
          console.log('Error updating booking slot ', err.response);
          let errorMessage = 'Error updating the booking slot';
          if (err && err.response && err.response.data) {
            if (err.response.data.error === ERROR_CODES.BOOKING_SLOTS.OVERLAP) {
              this.displayBookingSlotOverlapModal();
              return;
            } if (err.response.data.message) {
              errorMessage = err.response.data.message;
            } else if (err.response.data.error) {
              errorMessage = err.response.data.error;
            }
          }
          this.$toast.error(errorMessage, {
            toastClassName: ['toast-std', 'warning-toast'],
          });
        });
      } else {
        this.$toast.info('Please check you have entered a name for the booking slot');
      }
    },
    updateEndSlot() {
      if (this.limitedSlot) {
        const time = `${this.slotBeginHour}:${this.slotBeginMin}:00`;
        const addMinutes = moment(time, 'HH:mm:ss').add(this.max_slot_length, 'minutes').format('HH:mm');
        const split = addMinutes.split(':');

        this.slotEndHour = split[0];
        this.slotEndMin = split[1];
      } else {
        this.checkEndSlotValid();
      }
    },
    checkEndSlotValid() {
      let startTime = `${this.slotBeginHour}:${this.slotBeginMin}:00`;
      let endTime = `${this.slotEndHour}:${this.slotEndMin}:00`;

      startTime = moment(startTime, 'HH:mm:ss');
      endTime = moment(endTime, 'HH:mm:ss');

      const duration = moment.duration(endTime.diff(startTime));
      const minutesLeft = duration.asMinutes();

      const negative = Math.sign(minutesLeft) === -1;

      this.endDateValid = !negative && (minutesLeft <= this.max_slot_length);
    },
    increaseTimeSlot(prop, array, callback) {
      const value = this[prop];
      const max = array.length - 1;
      const index = array.indexOf(value);

      if (index < max) {
        this[prop] = array[index + 1];
      } else {
        this[prop] = array[0];
      }

      if (callback) {
        callback();
      }
    },
    decreaseTimeSlot(prop, array, callback) {
      const value = this[prop];
      const max = array.length - 1;
      const index = array.indexOf(value);

      if (index === 0) {
        this[prop] = array[max];
      } else {
        this[prop] = array[index - 1];
      }

      if (callback) {
        callback();
      }
    },
    resetStartEndDate() {
      this.start_date = '';
      this.end_date = '';
    },
  },
};
</script>
